import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  CONVERSION_VIEW_CURRENCY_KEY,
  CURRENCY_STORAGE_KEY,
  DEFAULT_CONVERSION_CURRENCY,
  DEFAULT_CURRENCY,
  formatCurrencyAmount,
  formatCurrencyLabel,
  getCurrencySymbol,
  isSupportedCurrency,
  loadConversionViewCurrency,
  loadStoredCurrency,
} from '../lib/currency';
import { useAuth } from './AuthContext';
import {
  convertWithRates,
  fetchExchangeRates,
  type ExchangeRates,
} from '../lib/exchangeRates';

interface CurrencyContextType {
  currency: string;
  symbol: string;
  conversionViewCurrency: string;
  setCurrency: (code: string) => void;
  setConversionViewCurrency: (code: string) => void;
  formatAmount: (amount: number, currencyCode?: string) => string;
  labelWithCurrency: (base: string) => string;
  convert: (amount: number, fromCode: string, toCode: string) => number | null;
  ratesReady: boolean;
  rates: ExchangeRates;
  ratesLoading: boolean;
  ratesError: string | null;
  refreshRates: () => Promise<void>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [currency, setCurrencyState] = useState<string>(loadStoredCurrency);
  const [conversionViewCurrency, setConversionViewState] = useState<string>(loadConversionViewCurrency);
  const [rates, setRates] = useState<ExchangeRates>({ EUR: 1 });
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState<string | null>(null);

  const refreshRates = useCallback(async () => {
    if (!localStorage.getItem('accessToken')) {
      setRatesLoading(false);
      setRatesError(null);
      return;
    }

    setRatesLoading(true);
    setRatesError(null);
    try {
      const next = await fetchExchangeRates();
      setRates(next);
    } catch (err) {
      setRatesError(err instanceof Error ? err.message : 'Could not load exchange rates');
    } finally {
      setRatesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setRatesLoading(false);
      setRatesError(null);
      return;
    }
    refreshRates();
  }, [authLoading, isAuthenticated, refreshRates]);

  useEffect(() => {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem(CONVERSION_VIEW_CURRENCY_KEY, conversionViewCurrency);
  }, [conversionViewCurrency]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === CURRENCY_STORAGE_KEY && event.newValue && isSupportedCurrency(event.newValue)) {
        setCurrencyState(event.newValue);
      }
      if (
        event.key === CONVERSION_VIEW_CURRENCY_KEY &&
        event.newValue &&
        isSupportedCurrency(event.newValue)
      ) {
        setConversionViewState(event.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setCurrency = useCallback((code: string) => {
    if (isSupportedCurrency(code)) {
      setCurrencyState(code);
    }
  }, []);

  const setConversionViewCurrency = useCallback((code: string) => {
    if (isSupportedCurrency(code)) {
      setConversionViewState(code);
    }
  }, []);

  const symbol = getCurrencySymbol(currency);

  const formatAmount = useCallback(
    (amount: number, currencyCode?: string) =>
      formatCurrencyAmount(amount, currencyCode ?? currency),
    [currency],
  );

  const labelWithCurrency = useCallback(
    (base: string) => formatCurrencyLabel(base, currency),
    [currency],
  );

  const convert = useCallback(
    (amount: number, fromCode: string, toCode: string) =>
      convertWithRates(amount, fromCode, toCode, rates),
    [rates],
  );

  const ratesReady = useMemo(
    () => Boolean(rates.INR && rates.INR > 10 && rates.USD),
    [rates],
  );

  const value = useMemo(
    () => ({
      currency,
      symbol,
      conversionViewCurrency,
      setCurrency,
      setConversionViewCurrency,
      formatAmount,
      labelWithCurrency,
      convert,
      ratesReady,
      rates,
      ratesLoading,
      ratesError,
      refreshRates,
    }),
    [
      currency,
      symbol,
      conversionViewCurrency,
      setCurrency,
      setConversionViewCurrency,
      formatAmount,
      labelWithCurrency,
      convert,
      ratesReady,
      rates,
      ratesLoading,
      ratesError,
      refreshRates,
    ],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextType {
  const context = useContext(CurrencyContext);
  if (!context) {
    return {
      currency: DEFAULT_CURRENCY,
      symbol: getCurrencySymbol(DEFAULT_CURRENCY),
      conversionViewCurrency: DEFAULT_CONVERSION_CURRENCY,
      setCurrency: () => undefined,
      setConversionViewCurrency: () => undefined,
      formatAmount: (amount, code) => formatCurrencyAmount(amount, code ?? DEFAULT_CURRENCY),
      labelWithCurrency: (base) => formatCurrencyLabel(base, DEFAULT_CURRENCY),
      convert: () => null,
      ratesReady: false,
      rates: { EUR: 1 },
      ratesLoading: false,
      ratesError: null,
      refreshRates: async () => undefined,
    };
  }
  return context;
}
